import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;

// Auth lives in an httpOnly cookie set by /api/admin/auth/login.
// `withCredentials: true` sends the cookie automatically on every request.
const http = axios.create({ withCredentials: true });

// Back-compat shim: a few callsites still import `adminToken`. All ops are
// no-ops now (the cookie is the only source of truth).
export const adminToken = {
    get: () => null,
    set: () => {},
    clear: () => {},
};

export const adminApi = {
    funnel: () =>
        http.get(`${ADMIN_API}/funnel`).then(r => r.data),
    setFunnelThreshold: (open_threshold) =>
        http.patch(`${ADMIN_API}/funnel/threshold`, { open_threshold }).then(r => r.data),
    aiModel: () =>
        http.get(`${ADMIN_API}/ai-model`).then(r => r.data),
    setAiModel: (provider, model) =>
        http.patch(`${ADMIN_API}/ai-model`, { provider, model }).then(r => r.data),
    markets: () =>
        http.get(`${ADMIN_API}/markets`).then(r => r.data),
    login: (email, password) =>
        http.post(`${ADMIN_API}/auth/login`, { email, password }).then(r => r.data),
    me: () => http.get(`${ADMIN_API}/auth/me`).then(r => r.data),
    logout: () => http.post(`${ADMIN_API}/auth/logout`, {}).then(r => r.data),
    stats: () => http.get(`${ADMIN_API}/stats`).then(r => r.data),
    costs: () => http.get(`${ADMIN_API}/costs`).then(r => r.data),
    users: (limit = 200, skip = 0, status = null) =>
        http.get(`${ADMIN_API}/users`, {
            params: { limit, skip, ...(status && status !== "all" ? { status } : {}) },
        }).then(r => r.data),
    exportUsersCsv: async (status = null) => {
        const url = `${ADMIN_API}/users/export.csv${status && status !== "all" ? `?status=${status}` : ""}`;
        const res = await http.get(url, { responseType: "blob" });
        const blob = new Blob([res.data], { type: "text/csv" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        const disp = res.headers["content-disposition"] || "";
        const m = disp.match(/filename="([^"]+)"/);
        a.download = m ? m[1] : `ever-after-users-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
    },
    unlockUser: (session_id) =>
        http.post(`${ADMIN_API}/users/${session_id}/unlock`, {}).then(r => r.data),
    lockUser: (session_id) =>
        http.post(`${ADMIN_API}/users/${session_id}/lock`, {}).then(r => r.data),
    referrals: (limit = 200) =>
        http.get(`${ADMIN_API}/referrals`, { params: { limit } }).then(r => r.data),
    archetypes: () =>
        http.get(`${ADMIN_API}/archetypes`).then(r => r.data),
    updateArchetype: (id, patch) =>
        http.patch(`${ADMIN_API}/archetypes/${id}`, patch).then(r => r.data),
    pricing: () =>
        http.get(`${ADMIN_API}/pricing`).then(r => r.data),
    updatePricing: (payload) =>
        http.patch(`${ADMIN_API}/pricing`, payload).then(r => r.data),
    accessCodes: () =>
        http.get(`${ADMIN_API}/access-codes`).then(r => r.data),
    createAccessCode: (payload) =>
        http.post(`${ADMIN_API}/access-codes`, payload).then(r => r.data),
    updateAccessCode: (code, patch) =>
        http.patch(`${ADMIN_API}/access-codes/${encodeURIComponent(code)}`, patch).then(r => r.data),
    deleteAccessCode: (code) =>
        http.delete(`${ADMIN_API}/access-codes/${encodeURIComponent(code)}`).then(r => r.data),
};

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map(e => e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
